import { create } from 'zustand';
import type { WorkareaTab, PaneState, PaneOrSplit, SplitNode, SplitDirection } from '@/types';

// ═══════════════════════════════════════════
// Helper functions
// ═══════════════════════════════════════════

function generatePaneId(): string {
  return `pane-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function generateSplitId(): string {
  return `split-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function findPaneById(root: PaneOrSplit, paneId: string): PaneState | null {
  if (root.type === 'pane') {
    return root.pane.id === paneId ? root.pane : null;
  }
  return findPaneById(root.children[0], paneId) ?? findPaneById(root.children[1], paneId);
}

function findPaneForTab(root: PaneOrSplit, tabId: string): PaneState | null {
  if (root.type === 'pane') {
    return root.pane.tabs.some((t) => t.id === tabId) ? root.pane : null;
  }
  return findPaneForTab(root.children[0], tabId) ?? findPaneForTab(root.children[1], tabId);
}

function updatePaneInTree(
  root: PaneOrSplit,
  paneId: string,
  updater: (pane: PaneState) => PaneState,
): PaneOrSplit {
  if (root.type === 'pane') {
    if (root.pane.id === paneId) {
      return { type: 'pane', pane: updater(root.pane) };
    }
    return root;
  }
  return {
    ...root,
    children: [
      updatePaneInTree(root.children[0], paneId, updater),
      updatePaneInTree(root.children[1], paneId, updater),
    ] as [PaneOrSplit, PaneOrSplit],
  };
}

/**
 * Remove a pane from the tree, returning the sibling that should replace the parent split.
 * Returns null if the pane was the only node (shouldn't happen normally).
 */
function removePaneFromTree(root: PaneOrSplit, paneId: string): PaneOrSplit | null {
  if (root.type === 'pane') {
    return root.pane.id === paneId ? null : root;
  }
  const [left, right] = root.children;

  // Check if the pane to remove is a direct child
  if (left.type === 'pane' && left.pane.id === paneId) {
    return right;
  }
  if (right.type === 'pane' && right.pane.id === paneId) {
    return left;
  }

  // Recurse into children
  const newLeft = removePaneFromTree(left, paneId);
  if (newLeft !== left) {
    // Found in left subtree
    if (newLeft === null) return right;
    return { ...root, children: [newLeft, right] as [PaneOrSplit, PaneOrSplit] };
  }

  const newRight = removePaneFromTree(right, paneId);
  if (newRight !== right) {
    // Found in right subtree
    if (newRight === null) return left;
    return { ...root, children: [left, newRight] as [PaneOrSplit, PaneOrSplit] };
  }

  return root;
}

function getAllPanes(root: PaneOrSplit): PaneState[] {
  if (root.type === 'pane') {
    return [root.pane];
  }
  return [...getAllPanes(root.children[0]), ...getAllPanes(root.children[1])];
}

function createDefaultRoot(): PaneOrSplit {
  return {
    type: 'pane',
    pane: { id: 'main', tabs: [], activeTabId: null },
  };
}

// ═══════════════════════════════════════════
// Store interface
// ═══════════════════════════════════════════

interface WorkspaceStore {
  root: PaneOrSplit;
  focusedPaneId: string;

  // Tab operations (work on focused pane by default, or specified pane)
  openTab: (tab: WorkareaTab, paneId?: string) => void;
  closeTab: (tabId: string, paneId?: string) => void;
  switchTab: (tabId: string) => void;
  updateTab: (tabId: string, updates: Partial<WorkareaTab>) => void;
  setFocusedPane: (paneId: string) => void;

  // Split operations
  splitPane: (paneId: string, direction: SplitDirection, tabIdToMove?: string) => void;
  closePane: (paneId: string) => void;
  setSplitRatio: (splitId: string, ratio: number) => void;
  moveTab: (tabId: string, fromPaneId: string, toPaneId: string, index?: number) => void;

  // Computed-like getters for backward compatibility
  // (These are computed on access from the focused pane.)
  readonly tabs: WorkareaTab[];
  readonly activeTabId: string | null;

  hydrate: (state: {
    root?: PaneOrSplit;
    focusedPaneId?: string;
    tabs?: WorkareaTab[];
    activeTabId?: string | null;
  }) => void;
}

// ═══════════════════════════════════════════
// Store implementation
// ═══════════════════════════════════════════

export const useWorkspaceStore = create<WorkspaceStore>((set, get) => ({
  root: createDefaultRoot(),
  focusedPaneId: 'main',

  // Backward compatibility getters — return focused pane's data
  get tabs(): WorkareaTab[] {
    const state = get();
    const pane = findPaneById(state.root, state.focusedPaneId);
    return pane?.tabs ?? [];
  },

  get activeTabId(): string | null {
    const state = get();
    const pane = findPaneById(state.root, state.focusedPaneId);
    return pane?.activeTabId ?? null;
  },

  openTab: (tab, paneId?) => {
    const { root, focusedPaneId } = get();
    const targetPaneId = paneId ?? focusedPaneId;

    // Check if tab already exists in any pane — just switch to it
    const existingPane = findPaneForTab(root, tab.id);
    if (existingPane) {
      set({
        root: updatePaneInTree(root, existingPane.id, (p) => ({
          ...p,
          activeTabId: tab.id,
        })),
        focusedPaneId: existingPane.id,
      });
      return;
    }

    set({
      root: updatePaneInTree(root, targetPaneId, (p) => ({
        ...p,
        tabs: [...p.tabs, tab],
        activeTabId: tab.id,
      })),
      focusedPaneId: targetPaneId,
    });
  },

  closeTab: (tabId, paneId?) => {
    const { root, focusedPaneId } = get();
    let targetPaneId = paneId;

    // If no paneId specified, find the pane that contains this tab
    if (!targetPaneId) {
      const pane = findPaneForTab(root, tabId);
      if (!pane) return;
      targetPaneId = pane.id;
    }

    // Record the closed tab in the session ring buffer so Cmd+P can
    // surface it under `@recent` for a quick reopen. Pushed here rather
    // than in the component so every close path (keyboard, context menu,
    // X button, bulk "close others") feeds the same buffer.
    const closingPane = findPaneById(root, targetPaneId);
    const closingTab = closingPane?.tabs.find((t) => t.id === tabId);
    if (closingTab) {
      // Dynamic import avoids a circular import with the command bar module graph
      import('@/lib/commands/recent-tab-store').then(({ useRecentTabStore }) => {
        useRecentTabStore.getState().pushClosed(closingTab);
      });
    }

    const newRoot = updatePaneInTree(root, targetPaneId, (p) => {
      const idx = p.tabs.findIndex((t) => t.id === tabId);
      if (idx === -1) return p;

      const newTabs = p.tabs.filter((t) => t.id !== tabId);
      let newActiveId = p.activeTabId;

      if (p.activeTabId === tabId) {
        if (newTabs.length === 0) {
          newActiveId = null;
        } else if (idx >= newTabs.length) {
          newActiveId = newTabs[newTabs.length - 1].id;
        } else {
          newActiveId = newTabs[idx].id;
        }
      }

      return { ...p, tabs: newTabs, activeTabId: newActiveId };
    });

    // Check if pane is now empty and there are other panes — remove it
    const updatedPane = findPaneById(newRoot, targetPaneId);
    if (updatedPane && updatedPane.tabs.length === 0) {
      const allPanes = getAllPanes(newRoot);
      if (allPanes.length > 1) {
        const collapsed = removePaneFromTree(newRoot, targetPaneId);
        if (collapsed) {
          // Focus the first remaining pane
          const remaining = getAllPanes(collapsed);
          const newFocused = remaining.length > 0 ? remaining[0].id : focusedPaneId;
          set({ root: collapsed, focusedPaneId: newFocused });
          return;
        }
      }
    }

    set({ root: newRoot });
  },

  switchTab: (tabId) => {
    const { root } = get();
    // Find which pane owns this tab and switch to it
    const pane = findPaneForTab(root, tabId);
    if (!pane) return;

    set({
      root: updatePaneInTree(root, pane.id, (p) => ({
        ...p,
        activeTabId: tabId,
      })),
      focusedPaneId: pane.id,
    });
  },

  updateTab: (tabId, updates) => {
    const { root } = get();
    const pane = findPaneForTab(root, tabId);
    if (!pane) return;

    set({
      root: updatePaneInTree(root, pane.id, (p) => ({
        ...p,
        tabs: p.tabs.map((t) =>
          t.id === tabId ? ({ ...t, ...updates } as WorkareaTab) : t,
        ),
      })),
    });
  },

  setFocusedPane: (paneId) => {
    set({ focusedPaneId: paneId });
  },

  splitPane: (paneId, direction, tabIdToMove?) => {
    const { root } = get();
    const newPaneId = generatePaneId();
    const splitId = generateSplitId();

    // Find the pane to split
    const pane = findPaneById(root, paneId);
    if (!pane) return;

    let originalPaneTabs = pane.tabs;
    let originalActiveTabId = pane.activeTabId;
    let newPaneTabs: WorkareaTab[] = [];
    let newPaneActiveTabId: string | null = null;

    // If tabIdToMove is specified, move that tab to the new pane
    if (tabIdToMove) {
      const tabToMove = pane.tabs.find((t) => t.id === tabIdToMove);
      if (tabToMove) {
        originalPaneTabs = pane.tabs.filter((t) => t.id !== tabIdToMove);
        newPaneTabs = [tabToMove];
        newPaneActiveTabId = tabToMove.id;

        // If the moved tab was active, select adjacent tab in original pane
        if (originalActiveTabId === tabIdToMove) {
          if (originalPaneTabs.length > 0) {
            const oldIdx = pane.tabs.findIndex((t) => t.id === tabIdToMove);
            const newIdx = Math.min(oldIdx, originalPaneTabs.length - 1);
            originalActiveTabId = originalPaneTabs[newIdx].id;
          } else {
            originalActiveTabId = null;
          }
        }
      }
    }

    const originalPaneNode: PaneOrSplit = {
      type: 'pane',
      pane: { id: paneId, tabs: originalPaneTabs, activeTabId: originalActiveTabId },
    };

    const newPaneNode: PaneOrSplit = {
      type: 'pane',
      pane: { id: newPaneId, tabs: newPaneTabs, activeTabId: newPaneActiveTabId },
    };

    const splitNode: SplitNode = {
      type: 'split',
      id: splitId,
      direction,
      children: [originalPaneNode, newPaneNode],
      ratio: 0.5,
    };

    // Replace the pane in the tree with the split
    const newRoot = replacePaneWithNode(root, paneId, splitNode);

    set({
      root: newRoot,
      focusedPaneId: tabIdToMove ? newPaneId : paneId,
    });
  },

  closePane: (paneId) => {
    const { root, focusedPaneId } = get();
    const allPanes = getAllPanes(root);

    // Can't close the last pane
    if (allPanes.length <= 1) return;

    const collapsed = removePaneFromTree(root, paneId);
    if (!collapsed) return;

    // If the focused pane was closed, focus the first remaining pane
    let newFocused = focusedPaneId;
    if (focusedPaneId === paneId) {
      const remaining = getAllPanes(collapsed);
      newFocused = remaining.length > 0 ? remaining[0].id : focusedPaneId;
    }

    set({ root: collapsed, focusedPaneId: newFocused });
  },

  setSplitRatio: (splitId, ratio) => {
    const { root } = get();
    const clamped = Math.max(0.1, Math.min(0.9, ratio));
    set({ root: updateSplitRatio(root, splitId, clamped) });
  },

  moveTab: (tabId, fromPaneId, toPaneId, index?) => {
    const { root } = get();

    const fromPane = findPaneById(root, fromPaneId);
    if (!fromPane) return;

    const tab = fromPane.tabs.find((t) => t.id === tabId);
    if (!tab) return;

    // Same-pane reorder. Drop the tab at `index` (or no-op if index missing
    // or equal to current position). Treated as a single-pane splice since
    // the cross-pane remove-then-insert dance below would incorrectly
    // shift the target index after the remove step.
    if (fromPaneId === toPaneId) {
      if (index === undefined) return;
      const currentIdx = fromPane.tabs.findIndex((t) => t.id === tabId);
      if (currentIdx === -1) return;
      // Adjust for the self-removal: if we're moving forward, the target
      // index collapses by 1 once the tab is removed.
      const targetIdx = index > currentIdx ? index - 1 : index;
      if (targetIdx === currentIdx) return;
      const newRoot = updatePaneInTree(root, fromPaneId, (p) => {
        const newTabs = [...p.tabs];
        const [moved] = newTabs.splice(currentIdx, 1);
        newTabs.splice(Math.max(0, Math.min(targetIdx, newTabs.length)), 0, moved);
        return { ...p, tabs: newTabs };
      });
      set({ root: newRoot });
      return;
    }

    // Remove from source pane
    let newRoot = updatePaneInTree(root, fromPaneId, (p) => {
      const newTabs = p.tabs.filter((t) => t.id !== tabId);
      let newActiveId = p.activeTabId;
      if (p.activeTabId === tabId) {
        const idx = p.tabs.findIndex((t) => t.id === tabId);
        if (newTabs.length === 0) {
          newActiveId = null;
        } else if (idx >= newTabs.length) {
          newActiveId = newTabs[newTabs.length - 1].id;
        } else {
          newActiveId = newTabs[idx].id;
        }
      }
      return { ...p, tabs: newTabs, activeTabId: newActiveId };
    });

    // Add to target pane
    newRoot = updatePaneInTree(newRoot, toPaneId, (p) => {
      const newTabs = [...p.tabs];
      if (index !== undefined && index >= 0 && index <= newTabs.length) {
        newTabs.splice(index, 0, tab);
      } else {
        newTabs.push(tab);
      }
      return { ...p, tabs: newTabs, activeTabId: tab.id };
    });

    // Check if source pane is now empty — collapse it
    const fromPaneAfter = findPaneById(newRoot, fromPaneId);
    if (fromPaneAfter && fromPaneAfter.tabs.length === 0) {
      const allPanes = getAllPanes(newRoot);
      if (allPanes.length > 1) {
        const collapsed = removePaneFromTree(newRoot, fromPaneId);
        if (collapsed) {
          set({ root: collapsed, focusedPaneId: toPaneId });
          return;
        }
      }
    }

    set({ root: newRoot, focusedPaneId: toPaneId });
  },

  hydrate: (state) => {
    if (state.root) {
      // New format
      set({
        root: state.root,
        focusedPaneId: state.focusedPaneId ?? getAllPanes(state.root)[0]?.id ?? 'main',
      });
    } else if (state.tabs) {
      // Legacy format — convert to single pane
      set({
        root: {
          type: 'pane',
          pane: {
            id: 'main',
            tabs: state.tabs,
            activeTabId: state.activeTabId ?? null,
          },
        },
        focusedPaneId: 'main',
      });
    }
  },
}));

// ═══════════════════════════════════════════
// Additional tree helpers
// ═══════════════════════════════════════════

/**
 * Replace a pane node in the tree with an arbitrary node (used for splitting).
 */
function replacePaneWithNode(root: PaneOrSplit, paneId: string, replacement: PaneOrSplit): PaneOrSplit {
  if (root.type === 'pane') {
    return root.pane.id === paneId ? replacement : root;
  }
  return {
    ...root,
    children: [
      replacePaneWithNode(root.children[0], paneId, replacement),
      replacePaneWithNode(root.children[1], paneId, replacement),
    ] as [PaneOrSplit, PaneOrSplit],
  };
}

function updateSplitRatio(root: PaneOrSplit, splitId: string, ratio: number): PaneOrSplit {
  if (root.type === 'pane') return root;
  if (root.id === splitId) {
    return { ...root, ratio };
  }
  return {
    ...root,
    children: [
      updateSplitRatio(root.children[0], splitId, ratio),
      updateSplitRatio(root.children[1], splitId, ratio),
    ] as [PaneOrSplit, PaneOrSplit],
  };
}

// Export helpers for use in components
export { findPaneById, findPaneForTab, getAllPanes };
